// src/components/ContactForm.jsx
//
// Setup required:
// 1. EmailJS  — https://emailjs.com
//    - Create account → Add a Gmail service (Service ID → paste below)
//    - Create a template with variables: {{from_email}}, {{subject}}, {{message}}, {{to_email}}
//    - Copy Service ID, Template ID, Public Key → paste into EMAILJS_ constants below
//
// 2. hCaptcha — https://hcaptcha.com
//    - Create account → Add site → copy Site Key → paste into HCAPTCHA_SITE_KEY below
//
// 3. Admin Parameters → set "Contact email" field to your recipient address.

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── ⚠ Paste your keys here ────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = 'service_ae126xk';
const EMAILJS_TEMPLATE_ID = 'template_2neuypa';
const EMAILJS_PUBLIC_KEY  = 'emTsXd_LFa2Ux2Zh-';
const HCAPTCHA_SITE_KEY   = 'c356ea12-32fe-40ec-8e1e-4f1c225e227e';
// ─────────────────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: '0.5px solid var(--sand-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13, fontFamily: "'Source Serif 4', serif",
  background: 'var(--sand-bg)', color: 'var(--sand-text)',
  outline: 'none', transition: 'border-color 0.15s',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 500,
  color: 'var(--sand-muted)',
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: '0.5px', marginBottom: 5,
  textTransform: 'uppercase',
};

const errorStyle = {
  fontSize: 11, color: '#fca5a5',
  fontFamily: "'JetBrains Mono', monospace",
  marginTop: 4,
};

export default function ContactForm({ contactEmail }) {
  const [form, setForm]       = useState({ email: '', subject: '', message: '' });
  const [errors, setErrors]   = useState({});
  const [captchaDone, setCaptchaDone] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(false);
  const [sendError, setSendError] = useState('');
  const captchaRef = useRef(null);
  const widgetId   = useRef(null);

  // Load hCaptcha script once
  useEffect(() => {
    if (window.hcaptcha) { renderCaptcha(); return; }
    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => renderCaptcha();
    document.head.appendChild(script);
    return () => { if (widgetId.current !== null && window.hcaptcha) {
      try { window.hcaptcha.reset(widgetId.current); } catch {}
    }};
  }, []);

  function renderCaptcha() {
    if (!captchaRef.current || widgetId.current !== null) return;
    try {
      widgetId.current = window.hcaptcha.render(captchaRef.current, {
        sitekey: HCAPTCHA_SITE_KEY,
        theme: 'dark',
        callback: (token) => { setCaptchaToken(token); setCaptchaDone(true); },
        'expired-callback': () => { setCaptchaToken(''); setCaptchaDone(false); },
      });
    } catch (e) { console.error('hCaptcha render error:', e); }
  }

  function validate() {
    const e = {};
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address.';
    if (!form.subject.trim()) e.subject = 'Subject is required.';
    if (!form.message.trim()) e.message = 'Message is required.';
    if (!captchaDone) e.captcha = 'Please complete the CAPTCHA.';
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSending(true);
    setSendError('');

    try {
      // Load EmailJS SDK dynamically
      if (!window.emailjs) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
          s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
      }
      window.emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
      await window.emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_email: form.email,
        subject:    form.subject,
        message:    form.message,
        to_email:   contactEmail || 'axces2000@gmail.com',
        'h-captcha-response': captchaToken,
      });
      setSent(true);
    } catch (err) {
      console.error('EmailJS error:', err);
      setSendError('Failed to send — please try again or email us directly.');
    } finally {
      setSending(false);
    }
  }

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: '' }));
  };

  // ── Success state ─────────────────────────────────────────────────────────
  if (sent) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
        <p style={{
          fontFamily: "'Playfair Display', serif", fontSize: 18,
          color: 'var(--sand-text)', marginBottom: 8,
        }}>Message sent!</p>
        <p style={{ fontSize: 13, color: 'var(--sand-muted)' }}>
          Thank you — we'll get back to you soon.
        </p>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Email */}
      <div>
        <label style={labelStyle}>Email <span style={{ color: '#fca5a5' }}>*</span></label>
        <input
          type="email" value={form.email}
          onChange={e => set('email', e.target.value)}
          placeholder="your@email.com"
          style={{ ...inputStyle, borderColor: errors.email ? '#fca5a5' : undefined }}
          onFocus={e => e.target.style.borderColor = errors.email ? '#fca5a5' : 'var(--sand-amber)'}
          onBlur={e => e.target.style.borderColor = errors.email ? '#fca5a5' : 'var(--sand-border)'}
        />
        {errors.email && <p style={errorStyle}>{errors.email}</p>}
      </div>

      {/* Subject */}
      <div>
        <label style={labelStyle}>Subject <span style={{ color: '#fca5a5' }}>*</span></label>
        <input
          type="text" value={form.subject}
          onChange={e => set('subject', e.target.value)}
          placeholder="What's this about?"
          style={{ ...inputStyle, borderColor: errors.subject ? '#fca5a5' : undefined }}
          onFocus={e => e.target.style.borderColor = errors.subject ? '#fca5a5' : 'var(--sand-amber)'}
          onBlur={e => e.target.style.borderColor = errors.subject ? '#fca5a5' : 'var(--sand-border)'}
        />
        {errors.subject && <p style={errorStyle}>{errors.subject}</p>}
      </div>

      {/* Message */}
      <div>
        <label style={labelStyle}>Message <span style={{ color: '#fca5a5' }}>*</span></label>
        <textarea
          value={form.message}
          onChange={e => set('message', e.target.value)}
          placeholder="Your message…"
          rows={5}
          style={{
            ...inputStyle, resize: 'vertical', lineHeight: 1.6,
            borderColor: errors.message ? '#fca5a5' : undefined,
          }}
          onFocus={e => e.target.style.borderColor = errors.message ? '#fca5a5' : 'var(--sand-amber)'}
          onBlur={e => e.target.style.borderColor = errors.message ? '#fca5a5' : 'var(--sand-border)'}
        />
        {errors.message && <p style={errorStyle}>{errors.message}</p>}
      </div>

      {/* hCaptcha */}
      <div>
        <div ref={captchaRef} />
        {errors.captcha && <p style={errorStyle}>{errors.captcha}</p>}
      </div>

      {/* Send error */}
      {sendError && (
        <p style={{ ...errorStyle, marginTop: 0 }}>{sendError}</p>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={sending}
        style={{
          padding: '10px 24px', alignSelf: 'flex-end',
          background: sending ? 'var(--sand-border)' : 'var(--sand-amber)',
          color: sending ? 'var(--sand-muted)' : '#0e1525',
          border: 'none', borderRadius: 'var(--radius-sm)',
          fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          cursor: sending ? 'not-allowed' : 'pointer',
          transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => { if (!sending) e.currentTarget.style.opacity = '0.85'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
      >
        {sending ? 'Sending…' : 'Send message →'}
      </button>
    </div>
  );
}
